import { logError } from '../utilities/logging';
import { fetchRetry } from './fetch';
import { getCart, ShopifyCart } from './getCart';
import { remove } from './remove';

interface AddToCartOptions {
  formData: FormData;
  onRetryStart: () => void;
  onRetryEnd: () => void;
  checkId: string;
  extraShouldRetryCheck?: ({ cartData }: { cartData: ShopifyCart }) => Promise<boolean>;
}

interface CartItemFeaturedImage {
  url: string;
  aspect_ratio: number;
  alt: string;
}

interface CartItemOptionWithValue {
  name: string;
  value: string;
}

interface CartItemProperties {
  _personalisation_choices?: string;
  _personalisation_base_attributes?: string;
  [key: string]: string | undefined;
}

interface CartItem {
  id: number;
  title: string;
  key: string;
  price: number;
  line_price: number;
  quantity: number;
  sku: string | null;
  grams: number;
  vendor: string;
  properties: CartItemProperties | null;
  variant_id: number;
  gift_card: boolean;
  url: string;
  featured_image: CartItemFeaturedImage;
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_title: string;
  product_description: string;
  product_type: string;
  variant_title: string;
  variant_options: string[];
  options_with_values: CartItemOptionWithValue[];
}

interface AddToCartResponse {
  response: {
    items: CartItem[];
  };
  cartHtml: string;
  cartItems: CartItem[];
  addedItemIds: number[];
}

const SHOPIFY_STORE_DOMAIN = /** @type {any} */ window.shopUrl;

export const add = async ({
  formData,
  onRetryStart,
  onRetryEnd,
  checkId
}: AddToCartOptions): Promise<AddToCartResponse> => {
  const response = await fetchRetry({
    url: `${SHOPIFY_STORE_DOMAIN}/cart/add.js`,
    options: {
      method: 'POST',
      body: formData
    },
    onRetryStart,
    onRetryEnd,
    extraShouldRetryCheck: ({ cartData }) => {
      const item = cartData.items.find((item) => item.properties?._check_id === checkId);
      return typeof item === 'undefined'; //* If the item is not in the cart, we should retry.
    },
    cleanUp: async () => {
      const cartData = await getCart();
      if ('error' in cartData) {
        return Promise.resolve(false);
      }
      const item = cartData.items.find((item) => item.properties?._check_id === checkId);
      if (item) {
        await remove({ key: item.key });
      }

      return Promise.resolve(true);
    },
    retryDelay: 2000,
    retries: 15
  });

  // Handle non-200 responses
  if (!response?.ok) {
    const errorText = (await response?.text()) || 'Failed to add items to cart';
    const status = response?.status || 0;
    console.error('[Personalisation addToCart] Cart add request failed:', {
      status,
      errorText,
      formData: Array.from(formData.entries())
    });
    // @TODO: handle these errors
    // https://shopify.dev/docs/api/ajax/reference/cart#error-responses
    logError({
      label: 'addToCart',
      error: new Error(`Cart request failed with status ${status}: ${errorText}`),
      message: `Cart request failed with status ${status}: ${errorText}`,
      extraData: {
        endpoint: `${SHOPIFY_STORE_DOMAIN}/cart/add.js`,
        formData: Array.from(formData.entries())
      },
      throwError: true
    });
    throw new Error(`Cart request failed with status ${status}: ${errorText}`);
  }

  const data = await response.json();

  return {
    response: data,
    addedItemIds: data.items.map((item: CartItem) => item.id),
    cartHtml: data.sections?.['cart-drawer'] || false,
    cartItems: data.items || []
  };
};
